import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tag, AlertCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Checkbox } from "../../../components/ui/Checkbox";

import {
  createSignupReference,
  updateSignupReference,
  type SignupReferenceView,
} from "../../../api/General/Subscription";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reference: SignupReferenceView | null;
  onSuccess: () => void;
}

/**
 * Add/Edit a signup-reference catalog entry. Slug is immutable after create —
 * any existing companies attributed to it would orphan otherwise.
 */
export default function SignupReferenceDialog({
  open,
  onOpenChange,
  reference,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const isEdit = reference != null;

  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSlug(reference?.slug ?? "");
    setName(reference?.name ?? "");
    setDescription(reference?.description ?? "");
    setIsActive(reference?.isActive ?? true);
    setError(null);
    setSubmitting(false);
  }, [open, reference]);

  const submit = async () => {
    if (submitting) return;
    const trimmedSlug = slug.trim().toLowerCase();
    const trimmedName = name.trim();
    if (!trimmedSlug) {
      setError(t("Slug is required."));
      return;
    }
    if (!trimmedName) {
      setError(t("Name is required."));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        slug: trimmedSlug,
        name: trimmedName,
        description: description.trim() || null,
        isActive,
      };
      const result = isEdit
        ? await updateSignupReference(reference!.id, payload)
        : await createSignupReference(payload);
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]?.message || result.errors[0]?.description || t("Failed to save reference"));
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch (e) {
      const message =
        (e as { response?: { data?: { errors?: { message?: string }[] } } })?.response?.data
          ?.errors?.[0]?.message || t("Failed to save reference");
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Tag size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{isEdit ? t("Edit reference") : t("Add reference")}</DialogTitle>
              <DialogDescription className="mt-1">
                {t("Slugs appear in signup URLs as ?ref=<slug>. Names are shown in the console only.")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="ref-slug">
              {t("Slug")}
            </label>
            <Input
              id="ref-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={isEdit}
              placeholder="mowazaf.com"
              autoComplete="off"
            />
            {isEdit ? (
              <p className="text-xs text-muted-foreground">
                {t("Slug cannot be changed after creation.")}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("Lowercase letters, digits, '-', '_' or '.' (2–64 chars).")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="ref-name">
              {t("Name")}
            </label>
            <Input
              id="ref-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("Campaign or partner name")}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="ref-desc">
              {t("Description")} <span className="text-muted-foreground font-normal">({t("Optional")})</span>
            </label>
            <textarea
              id="ref-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={isActive}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsActive(e.target.checked)}
            />
            <span>{t("Active — incoming signups using this slug link to this entry.")}</span>
          </label>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? t("Saving…") : isEdit ? t("Save changes") : t("Create reference")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
